<template>
  <base-hm-input :id="$attrs['id']"
                 :class="[
                   $attrs['class'],
                   { 'has-inline-icon-left': hasSlot('icon-inline-left') },
                   'has-inline-icon-right',
                   { 'component__hm-input-number--inline': inline },
                 ]"
                 :label="label"
                 :help="help"
                 :has-focus="hasFocus"
                 :is-empty="isValueEmpty"
                 :name="name"
                 :cols="cols"
                 :hidden="hidden"
                 :style="iconCountStyleVariables"
                 :disabled="disabledCombined">
    <input v-if="lazy"
           v-model.trim.lazy="inputValue"
           :data-vv-as="label"
           :data-vv-name="name"
           :disabled="disabledCombined"
           :autofocus="autofocus"
           class="hm-input__input hm-input__input--number"
           type="text"
           @focus="onFocus"
           @blur="onInputBlur">
    <input v-else
           v-model.trim="inputValue"
           :data-vv-as="label"
           :data-vv-name="name"
           :disabled="disabledCombined"
           :autofocus="autofocus"
           class="hm-input__input hm-input__input--number"
           type="text"
           @focus="onFocus"
           @blur="onInputBlur">
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper hm-input__icon__wrapper--left">
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-left"></slot>
    </div>
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper hm-input__icon__wrapper--right">
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-right"></slot>
    </div>
  </base-hm-input>
</template>

<script>

import { isNil, trim, isArray, isFunction, isFinite } from 'lodash';
import HmInputMixin from '@/mixins/HmInputMixin';
import parseDecimalNumber from 'parse-decimal-number';

export default {
  name: 'HmInputNumber',

  mixins: [HmInputMixin],

  props: {
    inline: {
      required: false,
      type: Boolean,
      default: false,
    },
    minFractionDigits: {
      required: false,
      type: Number,
      default: 0,
    },
    maxFractionDigits: {
      required: false,
      type: Number,
      default: 2,
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    lazy: {
      required: false,
      type: Boolean,
      default: false,
    },
    locale: {
      required: false,
      type: String,
      default: null,
    },
    autofocus: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  data: function () {
    return {
      hover: false,
      inputValue: '',
    };
  },


  computed: {
    defaultValue: function () {
      return this.required ? 0 : null;
    },
    iconCountLeft: function () {
      const slot = this.getSlot('icon-inline-left');

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    iconCountRight: function () {
      let slot = this.getSlot('icon-inline-right');
      slot = isNil(slot) || !isFunction(slot) ? slot : slot();

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    iconCountStyleVariables: function () {
      return {
        '--icons-left': this.iconCountLeft,
        '--icons-right': this.iconCountRight,
      };
    },
  },

  watch: {
    internalValue: function (newVal) {
      this.inputValue = isNil(this.internalValue) || !isFinite(this.internalValue) ? this.defaultValue : `${this.internalValue.toLocaleString(
        (this.locale || this.$i18n.locale),
        {
          minimumFractionDigits: this.minFractionDigits,
          maximumFractionDigits: this.maxFractionDigits,
        },
      )}`;
    },
    // TODO: update on locale change
  },

  created: function () {
    this.inputValue = isNil(this.internalValue) || !isFinite(this.internalValue) ? this.defaultValue : `${this.internalValue.toLocaleString(
      (this.locale || this.$i18n.locale),
      {
        minimumFractionDigits: this.minFractionDigits,
        maximumFractionDigits: this.maxFractionDigits,
      },
    )}`;
  },

  mounted: function () {
    this.$on('focus', () => {
      this.$el.querySelector('input').select();
    });
  },

  methods: {
    onInputBlur() {
      const options = {
        thousands: (9999).toLocaleString((this.locale || this.$i18n.locale), { minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[^\D]/g, ''),
        decimal: (0.1).toLocaleString((this.locale || this.$i18n.locale), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/[^\D]/g, ''),
      };

      const tempValue = isNil(this.inputValue) ? this.defaultValue : parseDecimalNumber(this.inputValue, options);

      this.internalValue = isNil(tempValue) || !isFinite(tempValue) ? this.defaultValue : tempValue;

      this.inputValue = isNil(this.internalValue) || !isFinite(this.internalValue) ? this.defaultValue : `${this.internalValue.toLocaleString(
        (this.locale || this.$i18n.locale),
        {
          minimumFractionDigits: this.minFractionDigits,
          maximumFractionDigits: this.maxFractionDigits,
        },
      )}`;

      this.$nextTick(this.onBlur);
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
   @import '~styles/components/lib/HmInput';
</style>

<style lang="scss">
  @import '~styles/import';

  .component__hm-input-number {
    &.number--right {
      input {
        text-align: right;
      }
    }
  }

  .table__cell__input {
    //margin-top: -0.3rem;
    .hm-input {
      padding: 0!important;
      margin: 0!important;
    }
    .hm-input__label {
      display: none;
    }
    input {
      //border: none!important;
      //box-shadow: none!important;
      //padding-right: 0.4rem!important;
      border-radius: 0!important;
      padding: 0.5rem 1rem 0.5rem!important;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmInputNumber.vue